import { useMemo } from 'react';
import { gql } from 'urql';

import { useQueryStable } from '@/hooks/useQueryStable';

/**
 * Custom query for ERD relationship tests that includes the `parents` field.
 * The standard `useDiscoveryAppliedTests` hook doesn't fetch parents, but we
 * need them to reliably identify both source and target models for relationship
 * tests — kwargs structure varies across dbt versions.
 */
const ErdTestsQuery = gql`
  query ErdTests(
    $first: Int!
    $after: String
    $environmentId: BigInt!
    $userId: BigInt
  ) {
    environment(id: $environmentId, userId: $userId) {
      applied {
        tests(first: $first, after: $after) {
          pageInfo {
            endCursor
            hasNextPage
          }
          edges {
            node {
              uniqueId
              name
              columnName
              model
              testMetadata {
                name
                kwargs
              }
              parents {
                uniqueId
              }
            }
          }
        }
      }
    }
  }
`;

export interface ErdTestNode {
  uniqueId: string;
  name: string | null;
  columnName: string | null;
  model: string | null;
  testMetadata: {
    name: string | null;
    kwargs: Record<string, unknown> | null;
  } | null;
  parents: Array<{ uniqueId: string }>;
}

interface ErdTestsData {
  environment: {
    applied: {
      tests: {
        edges: Array<{ node: ErdTestNode | null }>;
      };
    } | null;
  };
}

interface UseErdTestsParams {
  environmentId: number | undefined;
  userId: number | null;
}

export function useErdTests({ environmentId, userId }: UseErdTestsParams) {
  const [result] = useQueryStable<ErdTestsData>({
    query: ErdTestsQuery,
    variables: { first: 500, environmentId, userId },
    pause: !environmentId,
  });

  const tests = useMemo(() => {
    if (!result.data?.environment?.applied?.tests?.edges) return [];
    return result.data.environment.applied.tests.edges
      .map((edge) => edge.node)
      .filter((node): node is ErdTestNode => node !== null);
  }, [result.data]);

  return {
    tests,
    fetching: result.fetching,
    error: result.error,
  };
}
